import { PREDEFINED_ROLES } from '../app-constants/role.constant';
import { AuthUser } from '../entities/authUser.entity';
import {
  AccessPolicies,
  AccessPolicy,
  AccessPolicyType,
  PermissionLevelEnum,
  PermissionScopeEnum,
} from '../entities/role.entity';
import { isEqual } from 'lodash';
import { ROUTER } from '@/app-constants/router.constant';
import { TenantTypeEnum } from '@/entities/tenant.entity';

// Defines the tenant types that allow an external user to see the dropdown
// A user can view the dropdown if they belong to a 'parent' or 'partner' tenant type
export const ALLOWED_TENANT_TYPES = ['parent', 'partner'];

export const getUserFullName = (user: AuthUser): string => {
  if (!user) return '';
  return `${user.firstName} ${user.lastName}`.trim();
};

export const checkUserHasRoles = (user: AuthUser, roles: number[]): boolean => {
  const checkingRoles = new Set(roles);
  return user?.roles?.some((role) => checkingRoles.has(role.roleKey));
};
export const isAdministrator = (user: AuthUser): boolean => {
  // loop through user.roles to see if the user has the required role
  return user?.roles?.some((role) => role.isInternalAdmin);
};

export const isCustomerSuccessManager = (user: AuthUser): boolean => {
  return user?.roles?.some((role) => role.isCsm);
};

export const isPartnerTenant = (user: AuthUser): boolean => {
  return isEqual(user?.tenant?.tenantType, TenantTypeEnum.PARTNER);
};

export const isParentTenant = (user: AuthUser): boolean => {
  return user?.tenant?.tenantType === TenantTypeEnum.PARENT;
};

export const isMultiTenancyEnabled = (user: AuthUser): boolean => {
  return ALLOWED_TENANT_TYPES.includes(user?.tenant?.tenantType);
};

/**
 * Check if user has read access to the module with multi-tenancy support
 */
export const hasMultitenancyAccess = (user: AuthUser, accessPolicy: AccessPolicy): boolean =>
  hasAccess(user, accessPolicy) || hasRelatedAccess(user, accessPolicy);

/**
 * Check if the user has related access to the same module and permission
 */
export const hasRelatedAccess = (user: AuthUser, accessPolicy: AccessPolicy): boolean =>
  hasAccess(user, {
    ...accessPolicy,
    scope: PermissionScopeEnum.related,
  }) && isMultiTenancyEnabled(user);

export const hasRelatedPermission = (user: AuthUser): boolean => {
  return (user?.permissions || []).some((permission) => isEqual(permission.scope, PermissionScopeEnum.related));
};

export const hasAccess = (user: AuthUser, policy: AccessPolicy | AccessPolicies): boolean => {
  if (!policy) {
    return true;
  }
  // handle simple case where policy is an object
  if (!(policy as AccessPolicies).type) {
    const accessPolicy = policy as AccessPolicy;
    // loop through user.permissions to see if the user has the required permission
    return (user?.permissions || []).some((permission) => {
      let hasAccess =
        isEqual(permission?.module?.name?.toLowerCase(), accessPolicy.moduleName?.toLowerCase()) &&
        // 'all' permissionLevel means the user has all permissions
        (isEqual(permission.permissionLevel, accessPolicy.permission) ||
          isEqual(permission.permissionLevel, PermissionLevelEnum.all));

      if (accessPolicy.scope) {
        hasAccess = hasAccess && isEqual(permission.scope, accessPolicy.scope);
      }

      return hasAccess;
    });
  } else {
    // handle case where policy is an object
    const policies = policy as AccessPolicies;
    if (policies.type === AccessPolicyType.and) {
      return policies.policies.every((p) => hasAccess(user, p));
    } else if (policies.type === AccessPolicyType.or) {
      return policies.policies.some((p) => hasAccess(user, p));
    }
  }
};

export const getLetterAvatarImage = (user: AuthUser): string => {
  // if backgroundColor is missing
  // choose a random color from palette
  const text = `${user?.firstName[0]}${user?.lastName[0]}`.toUpperCase();
  const letterIndex = text[0].charCodeAt(0) - 64;
  const hue = (360 / 26) * letterIndex;
  const backgroundColor = `hsl(${hue}, 68%, 48%)`;
  // selects the foregroundColor (black or white)
  // based on the backgroundColor
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 2), 16);
  const b = parseInt(hex.slice(4, 2), 16);
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
  const theshold = 160;
  // sets the foreground color
  const foregroundColor = hsp > theshold ? '#000000' : '#ffffff';

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  canvas.width = 200;
  canvas.height = 200;

  // Draw background
  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, canvas.width, canvas.height);

  // Draw text
  // Get the computed style of the parent element
  const computedStyle = window.getComputedStyle(document.body);
  // Get the font property from the computed style
  const parentFont = computedStyle.getPropertyValue('font-family');

  context.font = `bold 100px ${parentFont}`;
  context.fillStyle = foregroundColor;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, canvas.width / 2, canvas.height / 2);
  return canvas.toDataURL('image/png');
};

export const getTokenExpirationTime = (token: string): number => {
  const tokenPayload = token.split('.')[1];
  const base64Url = tokenPayload.replace(/-/g, '+').replace(/_/g, '/');
  const base64 = decodeURIComponent(
    self
      .atob(base64Url)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );
  const payload = JSON.parse(base64);

  // Get the expiration time in seconds
  const expirationTime = payload.exp;
  const expirationTimeMs = expirationTime * 1000;

  // Return the expiration time as a Date object
  return expirationTimeMs;
};

export const getRoleLevelOfUser = (user: AuthUser): number => {
  if (checkUserHasRoles(user, [PREDEFINED_ROLES.SYSTEM_ADMIN.id])) {
    return PREDEFINED_ROLES.SYSTEM_ADMIN.level;
  }

  if (checkUserHasRoles(user, [PREDEFINED_ROLES.DH_ADMIN.id])) {
    return PREDEFINED_ROLES.DH_ADMIN.level;
  }

  // then check flag
  if (isAdministrator(user)) {
    return PREDEFINED_ROLES.DH_ADMIN.level;
  }

  if (checkUserHasRoles(user, [PREDEFINED_ROLES.CUSTOMER_SUCCESS_MANAGER.id])) {
    return PREDEFINED_ROLES.CUSTOMER_SUCCESS_MANAGER.level;
  }

  // then check flag
  if (isCustomerSuccessManager(user)) {
    return PREDEFINED_ROLES.CUSTOMER_SUCCESS_MANAGER.level;
  }

  if (hasAccess(user, ROUTER.CUSTOMER.USER_MANAGEMENT_PAGE.accessPolicy)) {
    return PREDEFINED_ROLES.CUSTOMER_ADMIN.level;
  }

  return PREDEFINED_ROLES.CUSTOMER.level;
};

export const hasHigherPrivileged = (user: AuthUser, comparedUser: AuthUser): boolean => {
  const levelOfCurrenUser = getRoleLevelOfUser(user);
  const levelOfComparedUser = getRoleLevelOfUser(comparedUser);

  if (!levelOfCurrenUser || !levelOfComparedUser) return false;

  return levelOfCurrenUser <= levelOfComparedUser;
};
